import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { InputModule } from '@sharedModule/components/input/input.module';
import { TRANSFER_TYPE } from '@sharedModule/constants/transfer-type.constant';
import { MaterialModule } from '@sharedModule/material/material.module';
import { Country } from '@sharedModule/models/country.model';
import { InputData } from '@sharedModule/models/input-data.model';
import { CommonService } from '@sharedModule/services/common/common.service';
import { ExcelExportService } from '@sharedModule/services/excel-export/excel-export.service';
import { ReportService } from '@sharedModule/services/report/report.service';
import {
  AMOUNT_OPERATORS,
  AMOUNT_TYPES,
} from '@sharedModule/services/report/report.types';
import { Subject, takeUntil } from 'rxjs';
import { ReportFilterDialogData } from './report-filter-dialog.model';

/**
 * Filter dialog for the sender / beneficiary reports. Collects optional filters,
 * calls the report endpoint and exports the returned rows to Excel. Launched
 * from the sender-list / recipient-list screens (no dedicated route needed).
 */
@Component({
  selector: 'app-report-filter-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MaterialModule, InputModule],
  templateUrl: './report-filter-dialog.component.html',
  styleUrl: './report-filter-dialog.component.scss',
})
export class ReportFilterDialogComponent implements OnInit, OnDestroy {
  readonly transferTypes = TRANSFER_TYPE;
  readonly amountOperators = AMOUNT_OPERATORS;
  readonly amountTypes = AMOUNT_TYPES;
  readonly field: Record<string, InputData> = {
    fromDate: { type: 'date', key: 'from_date', label: 'From Date' },
    toDate: { type: 'date', key: 'to_date', label: 'To Date' },
    amountType: { type: 'select', key: 'amount_type', label: 'Amount Type' },
    cumulativeAmount: {
      type: 'number',
      key: 'cumulative_amount',
      label: 'Cumulative Amount',
    },
    cumulativeAmountOp: {
      type: 'select',
      key: 'cumulative_amount_op',
      label: 'Amount Operator',
    },
    sourceCountry: {
      type: 'select',
      key: 'source_country',
      label: 'Source Country',
    },
    destinationCountry: {
      type: 'select',
      key: 'destination_country',
      label: 'Destination Country',
    },
    transferType: {
      type: 'select',
      key: 'transfer_type',
      label: 'Transfer Type',
    },
    countryOfBirth: {
      type: 'select',
      key: 'country_of_birth',
      label: 'Country of Birth',
    },
    beneficiaryId: {
      type: 'number',
      key: 'beneficiary_id',
      label: 'Beneficiary ID',
    },
  };

  filterForm!: FormGroup;
  countryList: Country[] = [];
  loading = false;
  #destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ReportFilterDialogData,
    private dialogRef: MatDialogRef<ReportFilterDialogComponent>,
    private commonService: CommonService,
    private reportService: ReportService,
    private excelExportService: ExcelExportService,
    private messageHandlerService: MessageHandlerService
  ) {}

  get isSender(): boolean {
    return this.data.type === 'sender';
  }

  ngOnInit(): void {
    this.filterForm = new FormGroup({
      from_date: new FormControl(''),
      to_date: new FormControl(''),
      amount_type: new FormControl(''),
      cumulative_amount: new FormControl(''),
      cumulative_amount_op: new FormControl(''),
      source_country: new FormControl(''),
      destination_country: new FormControl(''),
      transfer_type: new FormControl(''),
      country_of_birth: new FormControl(''),
      beneficiary_id: new FormControl(''),
    });
    this.commonService
      .getCountryList()
      .pipe(takeUntil(this.#destroy$))
      .subscribe(res => {
        if (res.success) this.countryList = res.response;
      });
  }

  generate(): void {
    const raw = this.filterForm.getRawValue();
    const payload = {
      ...raw,
      from_date: raw.from_date ? new Date(raw.from_date).toISOString() : '',
      to_date: raw.to_date ? new Date(raw.to_date).toISOString() : '',
      cumulative_amount:
        raw.cumulative_amount === '' || raw.cumulative_amount === null
          ? ''
          : Number(raw.cumulative_amount),
      beneficiary_id:
        raw.beneficiary_id === '' || raw.beneficiary_id === null
          ? ''
          : Number(raw.beneficiary_id),
      ...(this.data.partnerId ? { partner_id: this.data.partnerId } : {}),
    };

    this.loading = true;
    const report$ = this.isSender
      ? this.reportService.senderReport(payload)
      : this.reportService.beneficiaryReport(payload);
    report$.pipe(takeUntil(this.#destroy$)).subscribe({
      next: x => {
        this.loading = false;
        if (!x.success) return;
        const rows = Array.isArray(x.response)
          ? x.response
          : x.response
            ? [x.response]
            : [];
        if (rows.length) {
          this.excelExportService.exportToExcel(
            rows,
            `${this.data.type}_report`
          );
          this.messageHandlerService.show(`${rows.length} record(s) exported`);
          this.dialogRef.close(true);
        } else {
          this.messageHandlerService.show(
            'No records found for the selected filters'
          );
        }
      },
      error: () => (this.loading = false),
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
